import React, { useEffect } from 'react';
import { useRightSidebarController } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/right-sidebar-controller/index.tsx';
import { useIsCollectionView } from '@atlassian/jira-polaris-component-environment/src/PolarisEnvironmentProvider.tsx';
import { ProjectTemplateEnum } from '@atlassian/jira-polaris-domain-project/src/types.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	QUICKSTART_UI_STATUS_OPEN,
	QUICKSTART_PANEL_ID,
	QUICKSTART_PANEL_WIDTH,
} from '../services/constants.tsx';
import { useActions, useVisibility } from '../services/index.tsx';

import { allTasks } from '../tasks/allTasks.tsx';
import { OnboardingCheckList } from './OnboardingCheckList.tsx';

export const useQuickstartPanel = ({
	projectInitialTemplate,
	isViewContentReady,
}: {
	projectInitialTemplate: string | undefined;
	isViewContentReady: boolean;
}) => {
	const { openRightSidebar, closeRightSidebar } = useRightSidebarController();
	const { refreshAppState } = useActions();
	const isCollectionView = useIsCollectionView();
	const panelVisibility = useVisibility();

	useEffect(() => {
		if (
			!isViewContentReady ||
			isCollectionView ||
			!projectInitialTemplate ||
			projectInitialTemplate === ProjectTemplateEnum.BLANK ||
			panelVisibility !== QUICKSTART_UI_STATUS_OPEN
		) {
			return;
		}

		refreshAppState({
			tasks: {
				items: [
					{ id: 'explore-the-basics', completed: false },
					{ id: 'create-ideas', completed: false },
					{ id: 'prioritize-and-plan', completed: false },
					{ id: 'create-a-view', completed: false },
					{ id: 'share-and-collaborate', completed: false },
					{ id: 'link-work-items', completed: false },
				],
				title: {
					id: 'quickstart.tasks.title',
					defaultMessage: 'Quickstart',
				},
			},
		});
		openRightSidebar(QUICKSTART_PANEL_ID, QUICKSTART_PANEL_WIDTH, false, true);

		return () => {
			closeRightSidebar(QUICKSTART_PANEL_ID);
		};
	}, [
		refreshAppState,
		closeRightSidebar,
		isCollectionView,
		isViewContentReady,
		openRightSidebar,
		panelVisibility,
		projectInitialTemplate,
	]);
};

type TaskDefinition = { id: string; title: string; children: JSX.Element };

export const QuickstartPanel = () => {
	const { formatMessage } = useIntl();

	const taskDictionary = allTasks.reduce((acc: Record<string, TaskDefinition>, task) => {
		acc[task.id] = {
			id: task.id,
			title: formatMessage(task.title),
			children: task.description,
		};
		return acc;
	}, {});

	return <OnboardingCheckList taskDictionary={taskDictionary} />;
};
